<template>
  <div class="top-bar">
    <div class="left-section">
      <q-btn class="back-btn" flat round dense icon="arrow_back" size="md" @click="handleBackNavigation">
        <q-tooltip>{{ backTooltipText }}</q-tooltip>
      </q-btn>
      <h1 class="title-text">AI Image Editor</h1>
    </div>
    <div class="actions">
      <template v-if="hasImage && isGeneratedImage">
        <q-btn no-caps :loading="isDownloading" @click.native="handleDownload" flat class="action-btn" color="grey-6" padding="10px">
          <template v-slot:default>
            <q-icon name="o_download" class="q-mr-sm" color="grey-7" size="sm" />
            <q-tooltip>Download the image directly to your machine</q-tooltip>
            {{ isDownloading ? 'Downloading...' : 'Download' }}
          </template>
        </q-btn>
        <q-btn no-caps :loading="isSaving" @click="handleSave" flat class="action-btn" color="grey-6" padding="10px">
          <template v-slot:default>
            <q-icon name="o_save" class="q-mr-sm" color="grey-7" size="sm" />
            {{ isSaving ? 'Saving...' : 'Save as Version' }}
            <q-tooltip>Save a copy in your original's version history</q-tooltip>
          </template>
        </q-btn>
        <q-btn no-caps @click="handleDiscard" flat class="action-btn" color="negative" padding="10px">
          <template v-slot:default>
            <q-icon name="o_delete" class="q-mr-sm" color="surface" size="sm" />
            Discard
            <q-tooltip>Discard the current result</q-tooltip>
          </template>
        </q-btn>
      </template>
      <q-btn v-if="hasImage" no-caps @click="handleToggleHistory" flat class="action-btn" color="primary" padding="10px">
        <template v-slot:default>
          <q-icon name="o_history" class="q-mr-sm" color="primary" size="sm" />
          History
          <q-tooltip>Toggle generation history visibility</q-tooltip>
        </template>
      </q-btn>
    </div>
  </div>
</template>

<script>
import { ContentApi } from '@api/index';
import { useQueryClient } from '@tanstack/vue-query';

export default {
  name: 'ImageEditorTopBar',
  props: {
    isGeneratedImage: {
      type: Boolean,
      default: false
    },
    image: {
      type: Object,
      required: false,
      default: null
    },
    hasImage: {
      type: Boolean,
      default: false
    }
  },
  setup() {
    // Initialize TanStack Query client for Vue 2 compatibility
    const queryClient = useQueryClient();
    return { queryClient };
  },
  data() {
    return {
      isDownloading: false,
      isSaving: false
    };
  },
  methods: {
    // Helper function to extract version number from version name
    extractVersionNumber(versionName) {
      if (!versionName) return 0;
      // Match numbers with optional 'v' or 'V' prefix
      const match = String(versionName).match(/^v?(\d+)$/i);
      return match ? parseInt(match[1], 10) : 0;
    },

    // Get the next version number based on existing versions
    getNextVersionNumber(versions) {
      if (!Array.isArray(versions) || versions.length === 0) return 1;

      // Extract all version numbers and find the maximum
      const maxVersion = versions.reduce((max, version) => {
        const versionNum = this.extractVersionNumber(version.version_name);
        return versionNum > max ? versionNum : max;
      }, 0);

      return maxVersion + 1;
    },

    handleBackNavigation() {
      this.$emit('back-navigation');
    },
    async handleDownload() {
      if (this.isDownloading || !this.image) return;
      this.isDownloading = true;
      try {
        const response = await fetch(this.image.optimized_url);
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
        link.download = `edited-image-${timestamp}.jpeg`;
        link.style.display = 'none';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        this.$amplitude.trackEvent('Edit downloaded', {
          url
        });
      } catch (error) {
        console.error('Error downloading image:', error);
      } finally {
        this.isDownloading = false;
      }
    },
    async handleSave() {
      if (this.isSaving || !this.image) return;
      this.isSaving = true;
      try {
        const response = await fetch(this.image.optimized_url);
        const blob = await response.blob();
        const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
        const file = new File([blob], `edited-image-${timestamp}.jpeg`, { type: 'image/jpeg' });

        // Get image dimensions
        const img = new Image();
        await new Promise((resolve, reject) => {
          img.onload = resolve;
          img.onerror = reject;
          img.src = URL.createObjectURL(file);
        });

        const fileData = {
          name: file.name,
          relativePath: file.name,
          contentType: file.type,
          aspectRatio: parseFloat((img.naturalWidth / img.naturalHeight).toFixed(3))
        };

        // Get presigned URL
        const urlResponse = await ContentApi.getPresignedUrls({
          id: this?.image?.shoot_id || 0,
          files: [fileData]
        });

        const { presignedUrls, contentMetadata } = urlResponse.data;

        // Upload file
        const xhr = new XMLHttpRequest();
        await new Promise((resolve, reject) => {
          xhr.open('PUT', presignedUrls[0].presignedUrl, true);
          xhr.setRequestHeader('Content-Type', file.type);

          xhr.onload = () => (xhr.status === 200 ? resolve() : reject(new Error(`Upload failed: ${xhr.status}`)));
          xhr.onerror = () => reject(new Error('Network error during upload'));
          xhr.send(file);
        });

        // Create image batch
        const addContentResponse = await ContentApi.addContent({
          id: this?.image?.shoot_id || 0,
          payload: { content: contentMetadata }
        });

        // Get the newly created image ID
        const newImageId = addContentResponse.data?.content?.[0]?.id;
        if (!newImageId) {
          throw new Error('Failed to get new image ID from response');
        }

        this.$amplitude.trackEvent('Storage Used', {
          size: file.size
        });
        // Version management logic
        let stackId = this.image.stack_id;
        const originalImageId = this.image.id;
        let versionNumber = 1;

        // Create stack if original image doesn't have one
        if (!stackId) {
          const stackResponse = await ContentApi.createStack({
            description: `Create stack for ${this.image.filename || 'edited image'}`
          });
          stackId = stackResponse.data.stack.id;

          // Update the local image object with the new stack_id
          // so subsequent saves will use the existing stack
          this.image.stack_id = stackId;
        } else {
          // Fetch existing versions to calculate proper version number
          try {
            const versionsResponse = await ContentApi.getContentsByStackId({ stackId });
            const existingVersions = versionsResponse.data?.content || [];
            versionNumber = this.getNextVersionNumber(existingVersions);
          } catch (error) {
            console.warn('Failed to fetch existing versions, using default version number:', error);
            versionNumber = 1;
          }
        }

        // Add the new image to the stack as a version
        await ContentApi.addContentToStack({
          type: this.image.type,
          id: originalImageId,
          stackId: stackId,
          versionContentId: newImageId,
          versionName: `V${versionNumber}`,
          versionComment: 'Created via photo editor'
        });

        // Use TanStack Query to invalidate and refresh content
        this.queryClient.invalidateQueries({ queryKey: ['content'] });
        this.queryClient.invalidateQueries({ queryKey: ['versions', stackId] });

        // Clear lightbox state to prevent automatic return to lightbox after successful save
        // This allows users to stay in the AI editor and continue working if desired
        this.$store.dispatch('imagesEditor/clearLightboxStateOnly');
        this.$store.dispatch('notification/addSuccessNotification', 'Version saved successfully');

        this.$amplitude.trackEvent('Edit Saved as Version', {
          stackId,
          versionNumber,
          imageId: newImageId
        });
      } catch (error) {
        console.error('Error saving version:', error);
        this.$store.dispatch('notification/addFailureNotification', 'Failed to save version');
      } finally {
        this.isSaving = false;
      }
    },
    handleDiscard() {
      this.$emit('discard');
    },
    handleToggleHistory() {
      this.$emit('toggle-history-panel');
    }
  },
  computed: {
    // Get appropriate label for orders/shoots menu item based on user role
    backTooltipText() {
      return this.hasImage ? 'Back' : 'Back to previous';
    }
  }
};
</script>

<style lang="scss" scoped>
.top-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 32px;
  height: fit-content;
  min-height: 76px;
  max-height: 76px;
  background-color: white;
  overflow: visible;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  position: sticky;
  top: 0;
  z-index: 10;
  width: 100%;

  .left-section {
    display: flex;
    align-items: center;
    flex: 0 0 auto;
  }

  .title-text {
    @include inter-font($size: 18px, $bolded: true, $color: var(--main-text-color), $weight: 600);
    margin: 0 0 0 12px;
  }

  .back-btn {
    width: 40px;
    height: 40px;
    color: var(--main-text-color);
    transition: all 0.2s ease;

    &:hover {
      background: rgba(0, 0, 0, 0.05);
      color: var(--main-btn-color);
    }

    &:active {
      transform: scale(0.95);
    }
  }

  .actions {
    display: flex;
    gap: 12px;
    margin-left: auto;
    align-items: center;
    justify-content: flex-end;
    flex-wrap: nowrap;
    flex: 1 1 auto;
  }

  .action-btn {
    @include inter-font($size: 15px, $bolded: true, $color: var(--main-text-color));
    border-radius: 8px;
    min-height: 44px;
    height: 44px;
    padding: 0 18px;
    white-space: nowrap;
    transition: all 0.2s ease;
  }
}
</style>
